// Story 8.5 (ADO-2014), D-20: the choice screen's "Next cut-off" line and its "N more" list
// (Figma "Select Depot" frames, figma/8-5/2026-10-02).
//
// - The line names the cart (or carts, joined, when they share the exact deadline) whose
//   cut-off comes first, with its pill. "N more" is a disclosure that opens the list: one
//   row per cart with a deadline, soonest first and the soonest included, each with its
//   Dublin deadline ("Today 2:00pm", else "Thu 24 Sep, 10:00am") and its pill.
// - No line when no depot shown has a cut-off; no "N more" when nothing else is listed.
// - The list is a popover under the line, over the page, closed by Escape (focus back on
//   "N more") or a press outside. Desktop and tablet: date, name and pill in columns
//   (`columns`). Mobile: full width, each name over its deadline (`stacked`).
// - Every pill is the existing `CutOffPill` (`small`) from `useDepotCutOff`, so the line,
//   the list and the tiles share one read per cart. Story 8.4 (ADO-2013): a cart is the depot
//   and the account, so each Value Centre account is a row of its own with its own cut-off. No live region here: the page has no
//   `SiteHeader`, so no `CutOffAnnouncer` (D-20).

import { useEffect, useId, useRef, useState } from 'react';
import { CutOffPill, Icon } from '../../design-system';
import {
  cutOffRowKey,
  joinDepotNames,
  type CutOffDepot,
  type NextCutOffRow,
} from '../../basket/nextCutOffs';
import { useDepotCutOff } from '../../basket/useDepotCutOff';
import { useNextCutOffs } from '../../basket/useNextCutOffs';
import { formatDeadlineFromToday } from '../../shared/cutOffDisplay';
import styles from './ChoosePage.module.css';

export interface NextCutOffProps {
  /** The carts the page shows (depot and account), in the server's order (ties are named in
   *  it). */
  readonly depots: readonly CutOffDepot[];
  /** `columns` on desktop and tablet, `stacked` (name over deadline) on mobile. */
  readonly layout: 'columns' | 'stacked';
}

function NextCutOffListRow({
  row,
  layout,
}: {
  readonly row: NextCutOffRow;
  readonly layout: NextCutOffProps['layout'];
}) {
  const cutOff = useDepotCutOff({
    depotId: row.depotId,
    customerAccountCode: row.customerAccountCode ?? null,
  });
  const when = (
    <span className={styles.nextCutOffWhen}>
      {formatDeadlineFromToday(row.deadline, Date.now())}
    </span>
  );
  const name = <span className={styles.nextCutOffName}>{row.name}</span>;
  return (
    <li className={styles.nextCutOffRow}>
      {layout === 'stacked' ? (
        <span className={styles.nextCutOffRowText}>
          {name}
          {when}
        </span>
      ) : (
        <>
          {when}
          {name}
        </>
      )}
      <CutOffPill {...cutOff} />
    </li>
  );
}

export function NextCutOff({ depots, layout }: NextCutOffProps) {
  const next = useNextCutOffs(depots);
  const soonest = next?.soonest ?? [];
  const soonestRow = soonest[0];
  const cutOff = useDepotCutOff(
    soonestRow === undefined
      ? null
      : {
          depotId: soonestRow.depotId,
          customerAccountCode: soonestRow.customerAccountCode ?? null,
        },
  );
  const [open, setOpen] = useState(false);
  const labelId = useId();
  const listId = useId();
  const listRef = useRef<HTMLUListElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const more = next === null ? 0 : next.rows.length - soonest.length;
  // With nothing more to list (a refetch, or the line gone) the list is closed for good,
  // so it does not reopen by itself when "N more" comes back.
  if (open && more === 0) setOpen(false);
  const expanded = open && more > 0;

  // While open, Escape closes the list and returns focus to "N more", and a press anywhere
  // but the list closes it, the rest of the "Next cut-off" line included. A press on the
  // toggle is left to its own click, which toggles.
  useEffect(() => {
    if (!expanded) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      toggleRef.current?.focus();
    };
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (listRef.current?.contains(target) || toggleRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [expanded]);

  if (next === null) return null;

  return (
    <div className={styles.nextCutOff} data-layout={layout}>
      <div role="group" aria-labelledby={labelId} className={styles.nextCutOffLine}>
        <span id={labelId} className={styles.nextCutOffLabel}>
          Next cut-off
        </span>
        <CutOffPill {...cutOff} />
        <span className={styles.nextCutOffSoonest}>
          <span className={styles.nextCutOffNames}>
            {joinDepotNames(soonest.map((row) => row.name))}
          </span>
          {more > 0 && (
            <button
              ref={toggleRef}
              type="button"
              className={styles.nextCutOffMore}
              aria-expanded={expanded}
              aria-controls={expanded ? listId : undefined}
              aria-label={`${more} more ${more === 1 ? 'cut-off' : 'cut-offs'}`}
              onClick={() => setOpen((current) => !current)}
            >
              {more} more
              <span className={styles.nextCutOffCaret} data-open={expanded} aria-hidden>
                <Icon name="caretDown" size={14} />
              </span>
            </button>
          )}
        </span>
      </div>
      {expanded && (
        <ul ref={listRef} id={listId} className={styles.nextCutOffList} aria-label="Cut-offs">
          {next.rows.map((row) => (
            <NextCutOffListRow key={cutOffRowKey(row)} row={row} layout={layout} />
          ))}
        </ul>
      )}
    </div>
  );
}
